/**
 * MPP - Auto Trendlines - TV — sequential TradingView port (kept as the
 * previous Auto Trendlines engine). The default Auto Trendlines study is now
 * the Memory engine (`cueMemory.ts`); this file is the lean "Native Engine
 * Recreation" of TradingView's built-in Auto Trendlines (Pine v6). Unlike the
 * app's other Auto-Trendlines ports (`autoTrendlines.ts`, `srAuto.ts`, `srPrime.ts`), which
 * connect EVERY qualifying pivot pair and then score / overlap-resolve down to
 * the best few, this engine is SEQUENTIAL anchor-chaining:
 *
 *   - Two tiers: Large = 25/25 fractals protruding > 5×ATR(14),
 *                Small =  5/5  fractals protruding > 2×ATR(14).
 *   - Each qualifying pivot becomes the next anchor. A line is drawn between two
 *     CONSECUTIVE qualifying pivots only when the second continues the trend
 *     (resistance = a lower high, support = a higher low). So there is exactly
 *     ONE live line per tier/side at a time — a single evolving trendline that
 *     flips as new structure prints, not a fan of scored candidates.
 *   - A line is retired when a newer pivot supersedes it (ghost) or when price
 *     closes beyond it for `barsToBreakout` consecutive CLOSED bars (broken).
 *   - `keepHistory` archives retired lines (superseded faded, broken dotted);
 *     otherwise only the live lines remain.
 *
 * Pure — `AutoTrendlinesTvOverlay` turns these into screen coordinates.
 */
import type { Bar } from "@/lib/chart/indicatorTypes";
import { atrSeries } from "./srAuto";

export type AutoTlTvLine = {
  t1: number;
  p1: number;
  t2: number;
  p2: number;
  slope: number; // price per ms
  /** Right edge of the drawn segment — last bar (live), supersede bar, or breakout bar. */
  endT: number;
  type: "support" | "resistance";
  size: "small" | "large";
  broken: boolean;
  /** Bar time of the breakout (-1 when intact). */
  breakT: number;
  /** True when retired by a newer pivot (drawn as a faded ghost). */
  superseded: boolean;
  /** Anchor pivot times / prices (Show Pivots). */
  pivT: number[];
  pivP: number[];
};

export type AutoTlTvParams = {
  barsToBreakout?: unknown;
  lineSize?: unknown; // "both" | "small" | "large"
  showPivots?: unknown;
  showLabels?: unknown;
  showBreaks?: unknown;
  keepHistory?: unknown;
  largePivot?: unknown;
  largeAtr?: unknown;
  smallPivot?: unknown;
  smallAtr?: unknown;
  atrLen?: unknown;
  brokenExtendBars?: unknown;
  lookback?: unknown;
};

const num = (v: unknown, d: number) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};
const flag = (v: unknown, d: boolean) =>
  v === undefined || v === null ? d : !(v === false || v === 0 || v === "false" || v === "0" || v === "off");

type Active = { i1: number; y1: number; i2: number; y2: number; slope: number; brk: number };

/**
 * Simulate the Pine engine forward, bar by bar, for one tier + one side.
 * Mirrors the reference order within each bar: pivot construction FIRST, then
 * the breakout monitor on the (possibly new) active line at that same bar.
 */
function simulateSide(
  bars: Bar[],
  atr: number[],
  pLen: number,
  mult: number,
  isRes: boolean,
  size: "small" | "large",
  barsToBreak: number,
  keepHistory: boolean,
  start: number,
  n: number,
  live?: Array<number | null>,
): AutoTlTvLine[] {
  const out: AutoTlTvLine[] = [];
  const priceOf = (i: number) => (isRes ? bars[i]!.high : bars[i]!.low);
  const win = 2 * pLen;

  let lastIdx = -1;
  let lastY = NaN;
  let act: Active | null = null;

  const freeze = (a: Active, endIdx: number, broken: boolean, superseded: boolean) => {
    out.push({
      t1: bars[a.i1]!.t,
      p1: a.y1,
      t2: bars[a.i2]!.t,
      p2: a.y2,
      slope: a.slope,
      endT: bars[endIdx]!.t,
      type: isRes ? "resistance" : "support",
      size,
      broken,
      breakT: broken ? bars[endIdx]!.t : -1,
      superseded,
      pivT: [bars[a.i1]!.t, bars[a.i2]!.t],
      pivP: [a.y1, a.y2],
    });
  };

  // A candidate pivot at bar j is confirmed pLen bars later (at bar c = j + pLen).
  for (let c = 2 * pLen; c < n; c++) {
    // ── 1. Pivot confirmation + line construction ──────────────────────
    const j = c - pLen;
    if (j >= Math.max(start, pLen) && j + pLen < n) {
      const pv = priceOf(j);
      let frac = true;
      for (let k = j - pLen; k <= j + pLen; k++) {
        if (k === j) continue;
        if (isRes ? bars[k]!.high >= pv : bars[k]!.low <= pv) {
          frac = false;
          break;
        }
      }
      if (frac) {
        // Structural depth: the pivot must protrude from the last 2×pLen bars by
        // more than `mult` × ATR at the pivot bar (the ATR volatility filter).
        let ext = isRes ? Infinity : -Infinity;
        const lo = Math.max(0, c - win + 1);
        for (let k = lo; k <= c; k++) {
          if (isRes) ext = Math.min(ext, bars[k]!.low);
          else ext = Math.max(ext, bars[k]!.high);
        }
        const a = atr[j] || 0;
        const depth = isRes ? pv - ext : ext - pv;
        if (a > 0 && depth > mult * a) {
          // Qualifying pivot. Draw a line only when it continues the trend.
          if (lastIdx >= 0 && (isRes ? pv < lastY : pv > lastY)) {
            if (act && keepHistory) freeze(act, c, false, true); // superseded ghost, frozen here
            const t1 = bars[lastIdx]!.t;
            const t2 = bars[j]!.t;
            act = { i1: lastIdx, y1: lastY, i2: j, y2: pv, slope: (pv - lastY) / (t2 - t1), brk: 0 };
          }
          lastIdx = j;
          lastY = pv;
        }
      }
    }

    // ── 2. Breakout monitor on the active line (closed bars only) ───────
    if (act) {
      const lp = act.y1 + act.slope * (bars[c]!.t - bars[act.i1]!.t);
      if (live && Number.isFinite(lp)) live[c] = lp;
      if (lp > 0) {
        const beyond = isRes ? bars[c]!.close > lp : bars[c]!.close < lp;
        act.brk = beyond ? act.brk + 1 : 0;
        if (act.brk >= barsToBreak) {
          if (keepHistory) freeze(act, c, true, false); // broken, frozen at the confirming bar
          act = null;
        }
      }
    }
  }

  // Whatever is still active is the live line — extend to the last bar.
  if (act) freeze(act, n - 1, false, false);
  return out;
}

export function computeAutoTrendlinesTv(bars: Bar[], p: AutoTlTvParams = {}): AutoTlTvLine[] {
  const n = Array.isArray(bars) ? bars.length : 0;
  const barsToBreak = Math.max(1, Math.round(num(p.barsToBreakout, 3)));
  const lineSize = String(p.lineSize ?? "both");
  const wantLarge = lineSize !== "small";
  const wantSmall = lineSize !== "large";
  const largeL = Math.max(2, Math.round(num(p.largePivot, 25)));
  const largeMult = Math.max(0, num(p.largeAtr, 5));
  const smallL = Math.max(2, Math.round(num(p.smallPivot, 5)));
  const smallMult = Math.max(0, num(p.smallAtr, 2));
  const atrLen = Math.max(2, Math.round(num(p.atrLen, 14)));
  const keepHistory = flag(p.keepHistory, true);
  const lookback = Math.max(2 * largeL + 2, Math.round(num(p.lookback, 5000)));
  if (n < 2 * smallL + 2) return [];

  const start = Math.max(0, n - lookback);
  const atr = atrSeries(bars, atrLen);
  const out: AutoTlTvLine[] = [];

  if (wantLarge && n >= 2 * largeL + 2) {
    out.push(...simulateSide(bars, atr, largeL, largeMult, true, "large", barsToBreak, keepHistory, start, n));
    out.push(...simulateSide(bars, atr, largeL, largeMult, false, "large", barsToBreak, keepHistory, start, n));
  }
  if (wantSmall) {
    out.push(...simulateSide(bars, atr, smallL, smallMult, true, "small", barsToBreak, keepHistory, start, n));
    out.push(...simulateSide(bars, atr, smallL, smallMult, false, "small", barsToBreak, keepHistory, start, n));
  }

  // Live lines first, then broken, then superseded ghosts — mirrors the engine's
  // emphasis on the single evolving structure over retired history.
  const rank = (l: AutoTlTvLine) => (l.superseded ? 2 : l.broken ? 1 : 0);
  out.sort((a, b) => rank(a) - rank(b) || b.endT - a.endT);
  return out;
}

/** Per-bar interpolated price of the live Large resistance / support rails. */
export function autoTlLiveSeries(
  bars: Bar[],
  p: AutoTlTvParams = {},
): { largeRes: Array<number | null>; largeSup: Array<number | null> } {
  const n = Array.isArray(bars) ? bars.length : 0;
  const largeRes = new Array<number | null>(n).fill(null);
  const largeSup = new Array<number | null>(n).fill(null);
  if (n < 2) return { largeRes, largeSup };
  const barsToBreak = Math.max(1, Math.round(num(p.barsToBreakout, 3)));
  const largeL = Math.max(2, Math.round(num(p.largePivot, 25)));
  const largeMult = Math.max(0, num(p.largeAtr, 5));
  const atrLen = Math.max(2, Math.round(num(p.atrLen, 14)));
  const lookback = Math.max(2 * largeL + 2, Math.round(num(p.lookback, 5000)));
  if (n < 2 * largeL + 2) return { largeRes, largeSup };
  const start = Math.max(0, n - lookback);
  const atr = atrSeries(bars, atrLen);
  simulateSide(bars, atr, largeL, largeMult, true, "large", barsToBreak, false, start, n, largeRes);
  simulateSide(bars, atr, largeL, largeMult, false, "large", barsToBreak, false, start, n, largeSup);
  return { largeRes, largeSup };
}
